import { computed } from '@angular/core';
import { signalStore, withState, withMethods, patchState, withComputed } from '@ngrx/signals';
import { IGroupsWithMetaData } from 'polydraw';
import { getAreaTypeNameSingular, getDisplayName, getParentAreaIdFromKey, getParentAreaTypeFromKey } from 'src/app/area-builder/area-builder.utils';
import { AreaStructure } from 'src/models/area-builder/area-structure.model';
import { AreaType } from 'src/models/area-builder/area-type.enum';
import {
    Area,
    ExtendedAreaViewModel,
} from 'src/models/area-builder/area.model';

const initialState = {
    loadedStructures: {} as Record<number, Record<AreaType, AreaStructure>>,
    selectedAreas: <ExtendedAreaViewModel[]>[],
    previousSelectedArea: null as Area,
    selectedDrawnPolygons: <IGroupsWithMetaData[]>[],
    areAreasSaved: false,
    childrenCounter: {} as Record<string, number>, // areaId_areaType, count
    timeToGo: false // triggers exit animation
};

export const AreasStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods((store) => ({
        addDrawnPolygons(group: IGroupsWithMetaData) {
          patchState(store, (state) => ({ selectedDrawnPolygons: state.selectedDrawnPolygons.concat(group) }));
        },
        removeDrawnPolygons(group: IGroupsWithMetaData) {
          patchState(store, (state) => ({ selectedDrawnPolygons: state.selectedDrawnPolygons.filter(
            (g: IGroupsWithMetaData) => !(g.metaData?.general?.drawnId === group.metaData?.general?.drawnId)
          ) }));
        },
        addSelectedArea(area: Area, parentAreaKey: string): void {
            if (store.selectedAreas().some((s: Area) => s.areaId === area.areaId && s.areaType === area.areaType)) return;

            const mapped = {
              ...area,
              areaTypeName: getAreaTypeNameSingular(area.areaType),
              displayName: getDisplayName(area),
              parentAreaType: getParentAreaTypeFromKey(parentAreaKey),
              parentAreaId: getParentAreaIdFromKey(parentAreaKey),
              parentAreaKey
            };

            patchState(store, (state) => ({
                areAreasSaved: false,
                selectedAreas: [...state.selectedAreas, mapped],
                previousSelectedArea: area,
                childrenCounter: {
                    ...state.childrenCounter,
                    [parentAreaKey]: !!state.childrenCounter[parentAreaKey] ? state.childrenCounter[parentAreaKey] + 1 : 1
                }
            }));
        },
        removeSelectedArea(area: Area, parentAreaKey: string): void {
          patchState(store, (state) => ({
                areAreasSaved: false,
                previousSelectedArea: area,
                selectedAreas: state.selectedAreas.filter(
                    (s: Area) =>
                        !(
                            s.areaId === area.areaId &&
                            s.areaType === area.areaType
                        )
                ),
                childrenCounter: {
                    ...state.childrenCounter,
                    [parentAreaKey]: !!state.childrenCounter[parentAreaKey] ? state.childrenCounter[parentAreaKey] - 1 : 0
                }
            }));
        },
        removeAllSelectedAreas(): void {
            patchState(store, { selectedAreas: [], childrenCounter: {}, selectedDrawnPolygons: [] });
        },
        saveSelectedAreas(): void {
            patchState(store, () => ({ areAreasSaved: true }));
        },
        addLoadedStructure(
            areaId: number,
            areaType: AreaType,
            structure: AreaStructure
        ): void {
            patchState(store, (state) => ({
                loadedStructures: {
                    ...state.loadedStructures,
                    [areaId]: {
                        ...state.loadedStructures[areaId],
                        [areaType]: structure,
                    },
                },
            }));
        },
        getLoadedStructure(areaId: number, areaType: AreaType): AreaStructure {
            return store.loadedStructures()[areaId]?.[areaType];
        },
        setTimeToGo(value: boolean): void {
            patchState(store, { timeToGo: value });
        },
        resetPreviousSelectedArea() {
            patchState(store, { previousSelectedArea: null })
        },
        getPreviousSelectedArea(): Area {
            return store.previousSelectedArea();
        }
    }))
);
